import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ProductoService } from '../../services';

interface ImageCarouselProps {
  imagenPrincipal?: string;
  productoId: number;
  cantidadImagenes?: number;
  categoriaColor?: string;
  height?: number;
}

export function ImageCarousel({
  imagenPrincipal,
  productoId,
  cantidadImagenes = 0,
  categoriaColor = '#e0e0e0',
  height = 200,
}: ImageCarouselProps) {
  const [images, setImages] = useState<string[]>(
    imagenPrincipal ? [imagenPrincipal] : []
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const fetchedRef = useRef(false);

  useEffect(() => {
    console.log(`[ImageCarousel] productoId=${productoId}, cantidadImagenes=${cantidadImagenes}, hasImagenPrincipal=${!!imagenPrincipal}`);

    if ((cantidadImagenes ?? 0) > 1 && !fetchedRef.current) {
      console.log(`[ImageCarousel] Fetching more images for producto ${productoId}`);
      fetchedRef.current = true;
      ProductoService.getImagenes(productoId)
        .then((imagenesList) => {
          console.log(`[ImageCarousel] Fetched ${imagenesList?.length || 0} images`);
          const previews = imagenesList.map((img) => img.preview);
          console.log(`[ImageCarousel] Setting images: ${previews.length} images`);
          setImages(previews);
        })
        .catch((err) => {
          console.warn(`[ImageCarousel] Error loading images for producto ${productoId}:`, err);
        });
    }
  }, [productoId, cantidadImagenes, imagenPrincipal]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
  };

  const hasMultiple = images.length > 1;
  const currentImage = images[currentIndex];

  return (
    <Box
      sx={{
        position: 'relative',
        height: `${height}px`,
        backgroundColor: categoriaColor,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {currentImage && (
        <img
          src={currentImage}
          alt={`Producto imagen ${currentIndex + 1}`}
          key={`img-${currentIndex}`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            animation: 'fadeIn 0.2s ease-in-out',
          }}
        />
      )}

      {hasMultiple && (
        <>
          <IconButton
            onClick={handlePrev}
            aria-label="Imagen anterior"
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: '#fff',
              zIndex: 2,
              display: 'flex',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            <ArrowBackIosIcon fontSize="small" />
          </IconButton>

          <IconButton
            onClick={handleNext}
            aria-label="Siguiente imagen"
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: '#fff',
              zIndex: 2,
              display: 'flex',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </>
      )}

      {hasMultiple && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 2,
          }}
        >
          {images.map((_, index) => (
            <Box
              key={index}
              onClick={() => handleDotClick(index)}
              role="button"
              tabIndex={0}
              aria-label={`Ir a imagen ${index + 1}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDotClick(index);
                }
              }}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor:
                  index === currentIndex
                    ? '#a0588e'
                    : 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor:
                    index === currentIndex
                      ? '#a0588e'
                      : 'rgba(255, 255, 255, 0.9)',
                },
              }}
            />
          ))}
        </Box>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>
    </Box>
  );
}
